import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState({ mcpServers: [], workflows: [] });
  const [loading, setLoading] = useState(true);
  const [batchMode, setBatchMode] = useState(false);

  useEffect(() => {
    // Only load cart if user is authenticated
    if (user) {
      loadCart();
    } else {
      // Reset cart and stop loading if user is not authenticated
      setCart({ mcpServers: [], workflows: [] });
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadCart = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/cart');
      setCart(response.data.cart);
    } catch (error) {
      console.error('Error loading cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const addMCPServerToCart = async (mcpServerId) => {
    try {
      const response = await apiClient.post('/cart/mcp-servers', { mcpServerId });
      if (!batchMode) {
        setCart(response.data.cart);
      }
      return { success: true };
    } catch (error) {
      console.error('Error adding MCP server to cart:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to add MCP server to cart' 
      };
    }
  };

  const addWorkflowToCart = async (workflowId) => {
    try {
      const response = await apiClient.post('/cart/workflows', { workflowId });
      if (!batchMode) {
        setCart(response.data.cart);
      }
      return { success: true };
    } catch (error) {
      console.error('Error adding workflow to cart:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to add workflow to cart' 
      };
    }
  };

  const removeMCPServerFromCart = async (mcpServerId) => {
    try {
      const response = await apiClient.delete(`/cart/mcp-servers/${mcpServerId}`);
      if (!batchMode) {
        setCart(response.data.cart);
      }
      return { success: true };
    } catch (error) {
      console.error('Error removing MCP server from cart:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to remove MCP server from cart' 
      };
    }
  };

  const removeWorkflowFromCart = async (workflowId) => {
    try {
      const response = await apiClient.delete(`/cart/workflows/${workflowId}`);
      if (!batchMode) {
        setCart(response.data.cart);
      }
      return { success: true };
    } catch (error) {
      console.error('Error removing workflow from cart:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to remove workflow from cart' 
      };
    }
  };

  const clearCart = async () => {
    try {
      const response = await apiClient.delete('/cart');
      setCart(response.data.cart);
      return { success: true };
    } catch (error) {
      console.error('Error clearing cart:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to clear cart' 
      };
    }
  };

  const addToActiveProfile = async () => {
    try {
      const response = await apiClient.post('/cart/add-to-profile');
      setCart(response.data.cart);
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('Error adding cart to active profile:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to add cart to active profile' 
      };
    }
  };

  const addMCPServerToActiveProfile = async (mcpServerId) => {
    try {
      const response = await apiClient.post('/profiles/add-mcp-server', { mcpServerId });
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('Error adding MCP server to active profile:', error);
      // If the error is that the item already exists, treat it as success
      if (error.response?.data?.error?.includes('already in the active profile')) {
        return { 
          success: true, 
          message: 'MCP Server was already in the active profile' 
        };
      }
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to add MCP server to active profile' 
      };
    }
  };

  const addWorkflowToActiveProfile = async (workflowId) => {
    try {
      const response = await apiClient.post('/profiles/add-workflow', { workflowId });
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('Error adding workflow to active profile:', error);
      // If the error is that the item already exists, treat it as success
      if (error.response?.data?.error?.includes('already in the active profile')) {
        return { 
          success: true, 
          message: 'Workflow was already in the active profile' 
        };
      }
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to add workflow to active profile' 
      };
    }
  };

  // Batched profile operations to handle duplicates gracefully
  const addMultipleItemsToActiveProfile = async (mcpServerIds = [], workflowIds = []) => {
    try {
      const promises = [];
      
      // Add all MCP servers
      for (const mcpServerId of mcpServerIds) {
        promises.push(apiClient.post('/profiles/add-mcp-server', { mcpServerId }));
      }
      
      // Add all workflows
      for (const workflowId of workflowIds) {
        promises.push(apiClient.post('/profiles/add-workflow', { workflowId }));
      }
      
      // Wait for all operations to complete, but don't fail if some items already exist
      const results = await Promise.allSettled(promises);
      
      // Check if any operations succeeded
      const successfulResults = results.filter(result => result.status === 'fulfilled');
      const failedResults = results.filter(result => result.status === 'rejected');
      
      // Count how many were already in profile vs actual failures
      const alreadyInProfile = failedResults.filter(result => 
        result.reason?.response?.data?.error?.includes('already in the active profile')
      );
      const actualFailures = failedResults.filter(result => 
        !result.reason?.response?.data?.error?.includes('already in the active profile')
      );
      
      // Log any actual failures for debugging
      if (actualFailures.length > 0) {
        console.log(`${actualFailures.length} items failed to add to profile:`, 
          actualFailures.map(result => result.reason?.response?.data?.error || result.reason?.message));
      }
      
      // Return success if at least one item was added successfully, or if all items were already in profile
      const totalProcessed = successfulResults.length + alreadyInProfile.length;
      const message = successfulResults.length > 0 ? 
        `Successfully added ${successfulResults.length} items to profile` : 
        'All selected items were already in profile';
      
      return { 
        success: true, 
        message: totalProcessed > 0 ? message : 'No items to add to profile'
      };
    } catch (error) {
      console.error('Error adding multiple items to active profile:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to add items to active profile' 
      };
    }
  };

  // Batched cart operations to minimize re-renders
  const addMultipleItemsToCart = async (mcpServerIds = [], workflowIds = []) => {
    try {
      // Enable batch mode to prevent individual updates
      setBatchMode(true);
      
      const promises = [];
      
      // Add all MCP servers
      for (const mcpServerId of mcpServerIds) {
        promises.push(apiClient.post('/cart/mcp-servers', { mcpServerId }));
      }
      
      // Add all workflows
      for (const workflowId of workflowIds) {
        promises.push(apiClient.post('/cart/workflows', { workflowId }));
      }
      
      // Wait for all operations to complete, but don't fail if some items already exist
      const results = await Promise.allSettled(promises);
      
      // Check if any operations succeeded
      const successfulResults = results.filter(result => result.status === 'fulfilled');
      const failedResults = results.filter(result => result.status === 'rejected');
      
      // Log any failures for debugging, but don't treat them as errors
      if (failedResults.length > 0) {
        console.log(`${failedResults.length} items were already in cart or failed to add:`, 
          failedResults.map(result => result.reason?.response?.data?.error || result.reason?.message));
      }
      
      // Disable batch mode and reload cart once
      setBatchMode(false);
      await loadCart();
      
      // Return success if at least one item was added successfully, or if all items were already in cart
      return { 
        success: true, 
        message: successfulResults.length > 0 ? 
          `Successfully added ${successfulResults.length} items to cart` : 
          'All selected items were already in cart'
      };
    } catch (error) {
      // Ensure batch mode is disabled even on error
      setBatchMode(false);
      console.error('Error adding multiple items to cart:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to add items to cart' 
      };
    }
  };

  const removeMultipleItemsFromCart = async (mcpServerIds = [], workflowIds = []) => {
    try {
      // Enable batch mode to prevent individual updates
      setBatchMode(true);
      
      const promises = [];
      
      // Remove all MCP servers
      for (const mcpServerId of mcpServerIds) {
        promises.push(apiClient.delete(`/cart/mcp-servers/${mcpServerId}`));
      }
      
      // Remove all workflows
      for (const workflowId of workflowIds) {
        promises.push(apiClient.delete(`/cart/workflows/${workflowId}`));
      }
      
      // Wait for all operations to complete, but don't fail if some items are not in cart
      const results = await Promise.allSettled(promises);
      
      // Check if any operations succeeded
      const successfulResults = results.filter(result => result.status === 'fulfilled');
      const failedResults = results.filter(result => result.status === 'rejected');
      
      // Log any failures for debugging, but don't treat them as errors
      if (failedResults.length > 0) {
        console.log(`${failedResults.length} items were not in cart or failed to remove:`, 
          failedResults.map(result => result.reason?.response?.data?.error || result.reason?.message));
      }
      
      // Disable batch mode and reload cart once
      setBatchMode(false);
      await loadCart();
      
      // Return success if at least one item was removed successfully, or if all items were already not in cart
      return { 
        success: true, 
        message: successfulResults.length > 0 ? 
          `Successfully removed ${successfulResults.length} items from cart` : 
          'All selected items were already not in cart'
      };
    } catch (error) {
      // Ensure batch mode is disabled even on error
      setBatchMode(false);
      console.error('Error removing multiple items from cart:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to remove items from cart' 
      };
    }
  };

  const isInCart = (type, id) => {
    if (!cart) return false;
    if (type === 'mcpServer') {
      return cart.mcpServers?.some(server => server.id === id) || false;
    } else if (type === 'workflow') {
      return cart.workflows?.some(workflow => workflow.id === id) || false;
    }
    return false;
  };

  const getCartCount = () => {
    if (!cart) return 0;
    return (cart.mcpServers?.length || 0) + (cart.workflows?.length || 0);
  };

  const getMCPServerCount = () => {
    if (!cart) return 0;
    return cart.mcpServers?.length || 0;
  };

  const getWorkflowCount = () => {
    if (!cart) return 0;
    return cart.workflows?.length || 0;
  };

  const setBatchModeEnabled = (enabled) => {
    setBatchMode(enabled);
  };

  const value = {
    cart,
    loading,
    addMCPServerToCart,
    addWorkflowToCart,
    removeMCPServerFromCart,
    removeWorkflowFromCart,
    addMultipleItemsToCart,
    removeMultipleItemsFromCart,
    setBatchModeEnabled,
    clearCart,
    addToActiveProfile,
    addMCPServerToActiveProfile,
    addWorkflowToActiveProfile,
    addMultipleItemsToActiveProfile,
    isInCart,
    getCartCount,
    getMCPServerCount,
    getWorkflowCount,
    loadCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
